import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWizard } from '../../wizard';
import { InfoBox } from '../index';
import { contentApi } from '../../services/contentApi';
import { selfServiceApi } from '../../services/selfServiceApi';
import { useTimeSlots } from '../../hooks/useTimeSlots';
import { normalizeFlowId } from '../../wizard/flowRegistry';
import type { DayWithLocations } from '../../types';
import { formatDate, formatDateWithWeekday } from './timeSelectionUtils';

const DAYS_TO_LOAD_AT_ONCE = 3;

type SelectedState = { date?: string; time?: string; location?: number };
type ExpandedState = Record<string, boolean>;

interface TimeSlotDayProps {
  day: DayWithLocations;
  isNew: boolean;
  expanded: ExpandedState;
  onToggle: (key: string) => void;
  selected: SelectedState;
  onSelect: (date: string, time: string, locationId: number, locationName: string) => void;
}

const TimeSlotDay: React.FC<TimeSlotDayProps> = ({ day, isNew, expanded, onToggle, selected, onSelect }) => (
  <div className={`day-group ${isNew ? 'newly-added' : ''}`}>
    <h2 className="day-header">{formatDateWithWeekday(day.date)}</h2>
    {day.locations.map(location => {
      const key = `${day.date}_${location.id}`;
      const isExpanded = expanded[key];
      const timesToShow = isExpanded ? location.times : location.times.slice(0, 3);
      return (
        <div key={location.id} className="location-times">
          <h3>{location.name}</h3>
          {location.times.length > 0 ? (
            <>
              <div className="time-slots-grid">
                {timesToShow.map(time => {
                  const isSelected = selected.date === day.date && selected.time === time && selected.location === location.id;
                  return (
                    <button
                      key={time}
                      className={`time-slot ${isSelected ? 'selected' : ''}`}
                      onClick={() => onSelect(day.date, time, location.id, location.name)}
                      aria-label={`Vælg tidspunkt ${time} den ${formatDate(day.date)} på ${location.name}`}
                      data-testid={`time-slot-${day.date}-${time}-${location.id}`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
              {location.times.length > 3 && (
                <button
                  className="toggle-times-btn"
                  onClick={() => onToggle(key)}
                  aria-expanded={isExpanded}
                  aria-controls={`times-${day.date}-${location.id}`}
                >
                  {isExpanded ? 'Vis færre tider' : 'Vis flere tider'}
                </button>
              )}
            </>
          ) : (
            <p className="no-times-message">Ingen ledige tider denne dag</p>
          )}
        </div>
      );
    })}
  </div>
);

const useSelectedState = (date?: string, time?: string, location?: number): SelectedState => (
  useMemo(() => ({ date, time, location }), [date, time, location])
);

const TimeSelectionStep: React.FC = () => {
  const location = useLocation();
  const { selfServiceData, updateSelfServiceData, goNext, goBack, sessionId } = useWizard();
  const flowSegment = useMemo(() => location.pathname.split('/').filter(Boolean)[0] || null, [location.pathname]);
  const flowId = useMemo(() => normalizeFlowId(flowSegment), [flowSegment]);
  const { days, isLoading, error, refetch } = useTimeSlots(14);
  const [visibleDaysCount, setVisibleDaysCount] = useState(DAYS_TO_LOAD_AT_ONCE);
  const [lastVisibleCount, setLastVisibleCount] = useState(0);
  const [expandedLocations, setExpandedLocations] = useState<ExpandedState>({});
  const [body, setBody] = useState<string[] | null>(null);
  const formActionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentApi.get(flowId, 'select_time', 'da').then(c => {
      if (c?.body && c.body.length > 0) {
        setBody(c.body);
      }
    });
  }, [flowId]);

  const handleSelect = useCallback(async (date: string, time: string, locationId: number, locationName: string) => {
    updateSelfServiceData('date', date);
    updateSelfServiceData('time', time);
    updateSelfServiceData('location', locationId);
    updateSelfServiceData('locationName', locationName);
    if (sessionId) {
      try {
        await selfServiceApi.reserveTime(sessionId, date, time, locationId);
      } catch (err) {
        console.error('Failed to reserve time', err);
      }
    }
    requestAnimationFrame(() => formActionsRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }, [updateSelfServiceData, sessionId]);

  const toggleTimes = useCallback((key: string) => {
    setExpandedLocations(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleLoadMore = () => {
    setLastVisibleCount(visibleDaysCount);
    setVisibleDaysCount(count => count + DAYS_TO_LOAD_AT_ONCE);
    setTimeout(() => setLastVisibleCount(0), 1500);
  };

  const selected = useSelectedState(selfServiceData.date, selfServiceData.time, selfServiceData.location);
  const visibleDays = days.slice(0, visibleDaysCount);
  const canLoadMore = visibleDaysCount < days.length;

  if (isLoading) {
    return (
      <div className="step-content">
        <div className="loading-text">
          <div className="spinner" role="status" aria-label="Indlæser ledige tider..."></div>
          <span>Indlæser ledige tider...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="step-content">
        <div className="error-banner" role="alert">
          <i className="fa-solid fa-exclamation-circle"></i>
          <div>
            <strong>Fejl</strong>
            <p>{error}</p>
            <button onClick={refetch}>Prøv igen</button>
          </div>
        </div>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className="step-content">
        <p className="no-times-message">Der er desværre ingen ledige tider i den nærmeste fremtid. Prøv venligst igen senere.</p>
      </div>
    );
  }

  const handleNext = () => {
    if (selfServiceData.date && selfServiceData.time && selfServiceData.location !== undefined) {
      goNext();
    }
  };

  return (
    <div className="step-content">
      <h1>Vælg en tid</h1>
      {body && <InfoBox type="info">{body.map((p, index) => <p key={index}>{p}</p>)}</InfoBox>}
      <div id="time-overview-list">
        {visibleDays.map((day, index) => (
          <TimeSlotDay
            key={day.date}
            day={day}
            isNew={lastVisibleCount > 0 && index >= lastVisibleCount}
            expanded={expandedLocations}
            onToggle={toggleTimes}
            selected={selected}
            onSelect={handleSelect}
          />
        ))}
      </div>
      {canLoadMore && (
        <div className="load-more-container">
          <button onClick={handleLoadMore} className="btn btn-secondary">Vis flere dage</button>
        </div>
      )}
      <div ref={formActionsRef}></div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={goBack}>
          <i className="fa-solid fa-arrow-left"></i>
          Tilbage
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!selfServiceData.date || !selfServiceData.time || selfServiceData.location === undefined}
        >
          Fortsæt
          <i className="fa-solid fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

export default TimeSelectionStep;
